import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import type { FieldConfiguration } from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  ChartReportConfigurationForRunning,
  ChartStyleSettings,
  ConditionField,
  DimensionDescriptor,
  FinancialStatementReportConfigurationForRunning,
  ListReportConfigurationForRunning,
  PivotReportConfigurationForRunning,
  ReportField,
  SortField,
  StatementLineConfiguration,
  TabularReportConfigurationForRunning,
  TileReportConfigurationForRunning,
} from "../../../../../../../api/types/svTypes.ts";
import { ReportType } from "../../../../../../../api/types/svTypes.ts";
import { validateCondition } from "../../../store/utils/fieldsUtils.ts";
import {
  createListDefaultSettings,
  createPivotDefaultSettings,
  createTabularDefaultSettings,
  createTileDefaultSettings,
} from "../../../utilities/isConfigurationValid.ts";
import {
  initializeCommonRunState,
  isChartRunState,
  isFinancialStatementRunState,
  isListRunState,
  isPivotRunState,
  isTabularRunState,
  isTileRunState,
} from "./runStateUtils.ts";

export type RunStateBase = {
  //triggers a refresh of the grid
  refreshToken: string;
  dimensionNames: string[];
  //meta dimensions
  dimensions: DimensionDescriptor[];
  measureNames: string[];
  fieldsWithCustomConditionParameters: ReportField[];
};

export type ReportConfigurationForRunning =
  | TabularReportConfigurationForRunning
  | PivotReportConfigurationForRunning
  | ChartReportConfigurationForRunning
  | TileReportConfigurationForRunning
  | ListReportConfigurationForRunning
  | FinancialStatementReportConfigurationForRunning;

export type TabularRunState = TabularReportConfigurationForRunning & RunStateBase;
export type PivotRunState = PivotReportConfigurationForRunning & RunStateBase;
export type ChartRunState = ChartReportConfigurationForRunning & RunStateBase;
export type TileRunState = TileReportConfigurationForRunning & RunStateBase;
export type ListRunState = ListReportConfigurationForRunning & RunStateBase;
export type FinancialStatementRunState = FinancialStatementReportConfigurationForRunning & RunStateBase;

export type RunState =
  TabularRunState | PivotRunState | ChartRunState | TileRunState | ListRunState | FinancialStatementRunState;

export type RunStateAction =
  | {
      type: "Initialize";
      payload: {
        response: ReportConfigurationForRunning;
        reportId: string;
      };
    }
  | {
      type: "UpdateFieldConfig";
      payload: {
        field: ReportField;
        changes: Partial<FieldConfiguration>;
      };
    }
  | {
      type: "UpdateCondition";
      payload: {
        field: ConditionField;
        changes: Partial<ConditionField>;
      };
    }
  | {
      type: "UpdateSorting";
      payload: {
        newSorting: SortField[];
      };
    }
  | {
      type: "RefreshGrid";
    };

export const reducer = (state: RunState, action: RunStateAction): RunState => {
  switch (action.type) {
    case "Initialize": {
      if (isTabularRunState(action.payload.response)) {
        const { response } = action.payload;
        return initializeTabularRunState(response, action.payload.reportId);
      }
      if (isPivotRunState(action.payload.response)) {
        const { response } = action.payload;
        return initializePivotRunState(response, action.payload.reportId);
      }
      if (isChartRunState(action.payload.response)) {
        const { response } = action.payload;
        return initializeChartRunState(response, action.payload.reportId);
      }
      if (isTileRunState(action.payload.response)) {
        const { response } = action.payload;
        return initializeTileRunState(response, action.payload.reportId);
      }
      if (isListRunState(action.payload.response)) {
        const { response } = action.payload;
        return initializeListRunState(response, action.payload.reportId);
      }
      if (isFinancialStatementRunState(action.payload.response)) {
        const { response } = action.payload;
        return initializeFinancialStatementRunState(response, action.payload.reportId);
      }
      throw new Error("Invalid report type");
    }
    case "UpdateSorting": {
      const { newSorting } = action.payload;
      return {
        ...state,
        sort: newSorting,
      };
    }
    case "UpdateCondition": {
      const { field, changes } = action.payload;
      return {
        ...state,
        conditions: updateConditionItem(field, changes, state.conditions, state.reportType),
      };
    }
    case "UpdateFieldConfig": {
      if (isTileRunState(state)) {
        const stateCopy = structuredClone(state);
        const updatedFields = updateFieldConfig(stateCopy.fields, action);
        const updatedFieldsWithCustomConditionParameters = updateFieldConfig(
          stateCopy.fieldsWithCustomConditionParameters,
          action
        );
        return {
          ...stateCopy,
          fields: updatedFields,
          fieldsWithCustomConditionParameters: updatedFieldsWithCustomConditionParameters,
        };
      }
      if (isTabularRunState(state) || isListRunState(state)) {
        const stateCopy = structuredClone(state);
        const updatedFields = updateFieldConfig(stateCopy.fields, action);
        const updatedFieldsWithCustomConditionParameters = updateFieldConfig(
          stateCopy.fieldsWithCustomConditionParameters,
          action
        );
        return {
          ...stateCopy,
          fields: updatedFields,
          fieldsWithCustomConditionParameters: updatedFieldsWithCustomConditionParameters,
        };
      }
      if (isPivotRunState(state) || isChartRunState(state)) {
        const stateCopy = structuredClone(state);
        const updatedFields = updateFieldConfig(stateCopy.values, action);
        const updatedFieldsWithCustomConditionParameters = updateFieldConfig(
          stateCopy.fieldsWithCustomConditionParameters,
          action
        );
        return {
          ...stateCopy,
          fields: updatedFields,
          fieldsWithCustomConditionParameters: updatedFieldsWithCustomConditionParameters,
        };
      }
      if (isFinancialStatementRunState(state)) {
        const stateCopy = structuredClone(state);
        return {
          ...stateCopy,
          lineFields: updateFieldConfig(stateCopy.lineFields, action),
          fieldsWithCustomConditionParameters: updateFieldConfig(stateCopy.fieldsWithCustomConditionParameters, action),
          lines: updateLineFieldConfig(stateCopy.lines, action),
        };
      }
      throw new Error("Invalid report type");
    }
    case "RefreshGrid": {
      return {
        ...state,
        refreshToken: generateGuid(),
      };
    }
    default:
      throw new Error("Invalid action type");
  }
};

export const initializeTileRunState = (response: TileReportConfigurationForRunning, reportId: string) => {
  return {
    ...initializeCommonRunState({ type: "Initialize", payload: { response, reportId } }),
    settings: response.settings,
    fields: response.fields,
  };
};

export const initializeListRunState = (response: ListReportConfigurationForRunning, reportId: string) => {
  return {
    ...initializeCommonRunState({ type: "Initialize", payload: { response, reportId } }),
    settings: response.settings,
    fields: response.fields,
    sort: response.sort,
  };
};

export const initializeFinancialStatementRunState = (
  response: FinancialStatementReportConfigurationForRunning,
  reportId: string
): FinancialStatementRunState => {
  return {
    ...initializeCommonRunState({ type: "Initialize", payload: { response, reportId } }),
    settings: response.settings,
    lines: response.lines,
    columns: response.columns,
    lineFields: response.lineFields,
    dimensionsMeta: response.dimensionsMeta,
  };
};

export const initializeTabularRunState = (response: TabularReportConfigurationForRunning, reportId: string) => {
  return {
    ...initializeCommonRunState({ type: "Initialize", payload: { response, reportId } }),
    settings: response.settings,
    fields: response.fields,
    grouping: response.grouping,
    sort: response.sort,
  };
};

export const initializePivotRunState = (response: PivotReportConfigurationForRunning, reportId: string) => {
  return {
    ...initializeCommonRunState({ type: "Initialize", payload: { response, reportId } }),
    settings: response.settings,
    rows: response.rows,
    columns: response.columns,
    values: response.values,
    sort: response.sort,
  };
};

export const initializeChartRunState = (response: ChartReportConfigurationForRunning, reportId: string) => {
  return {
    ...initializeCommonRunState({ type: "Initialize", payload: { response, reportId } }),
    settings: response.settings,
    arguments: response.arguments,
    values: response.values,
    sort: response.sort,
  };
};

const updateFieldConfig = (fields: ReportField[], action: Extract<RunStateAction, { type: "UpdateFieldConfig" }>) => {
  const field = fields.find((f) => f.config.guid === action.payload.field.config.guid);
  if (field !== undefined) {
    Object.assign(field.config, action.payload.changes);
  }
  return fields;
};

const updateLineFieldConfig = (
  lines: StatementLineConfiguration[],
  action: Extract<RunStateAction, { type: "UpdateFieldConfig" }>
) => {
  const line = lines.find((l) => l.field?.guid === action.payload.field.config.guid);
  if (line?.field) {
    Object.assign(line.field, action.payload.changes);
  }
  return lines;
};

const updateConditionItem = (
  item: ConditionField,
  changedValues: Partial<ConditionField>,
  fields: ConditionField[],
  reportType: ReportType
) => {
  const copy = structuredClone(fields);
  const field = copy.find((condition) => condition.config.guid === item.config.guid);
  if (field !== undefined) {
    Object.assign(field, changedValues);
    copy.forEach((condition) => {
      condition.validation = validateCondition(condition, copy, reportType);
    });
  }
  return copy;
};

export const getInitialTabularState = (): TabularRunState => {
  return {
    dataSetId: "",
    dimensions: [],
    reportType: ReportType.Tabular,
    settings: createTabularDefaultSettings(),
    fields: [],
    grouping: [],
    conditions: [],
    sort: [],
    showParameters: false,
    fieldsWithCustomConditionParameters: [],
    refreshToken: "",
    dimensionNames: [],
    measureNames: [],
    books: [],
    isMultiBook: false,
    fieldIdsWithCustomConditionParameters: [],
    customConditionsMeta: [],
  };
};

export const getInitialPivotState = (): PivotRunState => {
  return {
    dataSetId: "",
    dimensions: [],
    reportType: ReportType.Pivot,
    showParameters: false,
    fieldsWithCustomConditionParameters: [],
    refreshToken: "",
    dimensionNames: [],
    measureNames: [],
    books: [],
    isMultiBook: false,
    conditions: [],
    rows: [],
    columns: [],
    values: [],
    sort: [],
    settings: createPivotDefaultSettings(),
    fieldIdsWithCustomConditionParameters: [],
    customConditionsMeta: [],
  };
};

export const getInitialChartState = (reportType: ReportType): ChartRunState => {
  return {
    dataSetId: "",
    dimensions: [],
    reportType: reportType,
    arguments: [],
    values: [],
    conditions: [],
    sort: [],
    showParameters: false,
    fieldsWithCustomConditionParameters: [],
    refreshToken: "",
    dimensionNames: [],
    measureNames: [],
    books: [],
    isMultiBook: false,
    settings: {
      type: reportType,
    } as ChartStyleSettings,
    fieldIdsWithCustomConditionParameters: [],
    customConditionsMeta: [],
  };
};

export const getInitialListState = (): ListRunState => {
  return {
    dataSetId: "",
    dimensions: [],
    reportType: ReportType.List,
    settings: createListDefaultSettings(),
    fields: [],
    conditions: [],
    sort: [],
    showParameters: false,
    fieldsWithCustomConditionParameters: [],
    refreshToken: "",
    dimensionNames: [],
    measureNames: [],
    books: [],
    isMultiBook: false,
    fieldIdsWithCustomConditionParameters: [],
    customConditionsMeta: [],
  };
};

export const getInitialTileState = (): TileRunState => {
  return {
    dataSetId: "",
    dimensions: [],
    reportType: ReportType.Tile,
    settings: createTileDefaultSettings(),
    fields: [],
    conditions: [],
    showParameters: false,
    fieldsWithCustomConditionParameters: [],
    refreshToken: "",
    dimensionNames: [],
    measureNames: [],
    books: [],
    isMultiBook: false,
    fieldIdsWithCustomConditionParameters: [],
    customConditionsMeta: [],
  };
};
